import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { AlulaReceiver } from "@app/project/model/alula-receiver";
import { domainIsValid } from "@app/shared/validators";
import { AppErrorStateMatcher } from "@app/shared/validators/app-error-state-matcher.service";
import { ReceiverDescription, ReceiverTypes } from "../central-station.model";

interface ReceiverField {
    label: string;
    controlName: string;
    showField: boolean;
    required?: boolean;
    validDomainRequired?: boolean;
    type?: string;
}

@Component({
    selector: "app-alula-receivers-group-input",
    templateUrl: "./alula-receiver-input.component.html",
    styleUrls: ["./alula-receiver-input.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AlulaReceiverInputComponent implements OnInit {
    @Output() emitter: EventEmitter<any> = new EventEmitter<any>();
    @Input() description: string;
    @Input() receiver: AlulaReceiver;
    @Input() dealerId: string;
    passwordTouched = false;
    timesPasswordTouched = 0;

    readonly RAPID_RESPONSE_WEST_PATH = "https://west.rrmsalarm.com/AlulaWestProd";
    readonly RAPID_RESPONSE_EAST_PATH = "https://east.rrmsalarm.com/AlulaEastProd";

    dnisTemplate = "00000";
    public form: FormGroup;

    public surGardFields = [
        { label: "ID", controlName: "id", showField: false, required: false },
        { label: "Name", controlName: "name", showField: false, required: true },
        { label: "Communication Type", controlName: "receiverType", showField: false, required: true },
        { label: "IP Address", controlName: "path", showField: true, required: true, type: "text" },
        {
            label: "DNIS Number",
            controlName: "dnis",
            showField: true,
            required: true,
            type: "text",
            blurMethod: () => this.blurHandler("dnis"),
            inputMethod: () => this.filterNumbers5Digits("dnis"),
        },
        { label: "Description", controlName: "description", showField: false, required: false },
    ];
    public stagesFields = [
        { label: "ID", controlName: "id", showField: false, required: false },
        { label: "Name", controlName: "name", showField: false, required: true, type: "text" },
        { label: "Communication Type", controlName: "receiverType", showField: false, required: true },
        { label: "Username", controlName: "username", showField: true, required: true, type: "text" },
        { label: "Password", controlName: "passwd", showField: true, required: true, type: "password" },
        { label: "Receiver Path", controlName: "path", showField: true, required: true, type: "text", validDomainRequired: true },
        {
            label: "DNIS Number",
            controlName: "lineNum",
            showField: true,
            required: true,
            filterDNIS: true,
            blurMethod: () => this.blurHandler("lineNum"),
            inputMethod: () => this.filterNumbers5Digits("lineNum"),
        },
        { label: "Description", controlName: "description", showField: false, required: false },
    ];
    public fields;
    public rcvTypes = [
        { name: "SurGuard IP", value: ReceiverTypes.sgtcp },
        { name: "Stages XML", value: ReceiverTypes.stages },
        { name: "Rapid Response West", value: ReceiverTypes.rapidRspWest },
        { name: "Rapid Response East", value: ReceiverTypes.rapidRspEast },
    ];

    selectedOption1: string = ReceiverTypes.rapidRspWest;

    constructor(public errorMatcher: AppErrorStateMatcher, private readonly fb: FormBuilder) {}

    ngOnInit() {
        this.form = this.fb.group({
            receiverType: [""],
        });

        // Watch for changes in selector
        this.form.get("receiverType").valueChanges.subscribe(value => {
            this.setFields(
                value === ReceiverTypes.stages || value === ReceiverTypes.rapidRspWest || value === ReceiverTypes.rapidRspEast
                    ? this.stagesFields
                    : this.surGardFields,
                value
            );
        });

        this.setupFormAndType();

        const { receiverType, ...receiverData } = this.receiver;

        if (this.receiver.receiverType === ReceiverTypes.sgtcp) {
            const dnis = this.getDNISFromReceiver(this.receiver);
            this.form.patchValue(this.receiver);
            this.form.patchValue({ dnis });
        } else if (this.receiver.receiverType === ReceiverTypes.stages) {
            this.form.patchValue({ ...receiverData });
            this.blurHandler("lineNum");
        }
    }

    private setupFormAndType() {
        if (!this.receiver.receiverType) {
            this.setFields(this.surGardFields, ReceiverTypes.sgtcp);
            this.form.get("receiverType").setValue(ReceiverTypes.sgtcp);
            return;
        }
        if (this.isRapidResponse(this.receiver)) {
            this.setFields(this.stagesFields, ReceiverTypes.rapidRspWest);
            this.form.get("receiverType").setValue(ReceiverTypes.rapidRspWest);
            return;
        }
        if (this.isRapidResponseEast(this.receiver)) {
            this.setFields(this.stagesFields, ReceiverTypes.rapidRspEast);
            this.form.get("receiverType").setValue(ReceiverTypes.rapidRspEast);
            return;
        }

        this.form.get("receiverType").setValue(this.receiver.receiverType);
    }

    private isRapidResponse(receiver: AlulaReceiver) {
        return (
            (receiver.receiverType === ReceiverTypes.stages || receiver.receiverType === ReceiverTypes.rapidRspWest) &&
            (this.checRapidResponseType(receiver.description, ReceiverDescription.RR_WEST) ||
                this.checRapidResponseType(receiver.description, ReceiverDescription.RR_DEFAULT))
        );
    }

    private isRapidResponseEast(receiver: AlulaReceiver) {
        return (
            (receiver.receiverType === ReceiverTypes.stages || receiver.receiverType === ReceiverTypes.rapidRspEast) &&
            this.checRapidResponseType(receiver.description, ReceiverDescription.RR_EAST)
        );
    }

    private checRapidResponseType(
        receiverDesc: string,
        comparor: ReceiverDescription.RR_EAST | ReceiverDescription.RR_WEST | ReceiverDescription.RR_DEFAULT
    ) {
        return (receiverDesc || "").toLowerCase() === comparor.toLocaleLowerCase();
    }

    setFields(fieldsToCreate: ReceiverField[], receiverType: ReceiverTypes) {
        this.fields = fieldsToCreate;
        this.selectedOption1 = receiverType;
        Object.keys(this.receiverFields).forEach(field => {
            if (field !== "receiverType" && field !== "id" && field !== "description") {
                this.form.removeControl(field);
            }
        });

        fieldsToCreate.forEach(field => {
            if (!this.receiverFields?.[field.controlName]) {
                const validators = [];
                if (field.required) {
                    validators.push(Validators.required);
                }
                if (field.validDomainRequired) {
                    validators.push(domainIsValid);
                }
                this.form.addControl(field.controlName, this.fb.control("", validators));
            }
        });
        this.form.get("name").setValue(receiverType === ReceiverTypes.sgtcp ? "IP Receiver" : "Stages XML Receiver");

        if (receiverType === ReceiverTypes.sgtcp) {
            this.form.get("description").setValue(ReceiverDescription.IP);
        } else if (receiverType === ReceiverTypes.rapidRspWest) {
            this.form.patchValue({
                description: ReceiverDescription.RR_WEST,
                path: this.RAPID_RESPONSE_WEST_PATH,
            });
            this.updateFormFieldForRapidResponse();
        } else if (receiverType === ReceiverTypes.rapidRspEast) {
            this.form.patchValue({
                description: ReceiverDescription.RR_EAST,
                path: this.RAPID_RESPONSE_EAST_PATH,
            });
            this.updateFormFieldForRapidResponse();
        } else {
            this.form?.get("description").setValue(ReceiverDescription.STAGE_XML);
        }
        this.form.get("passwd")?.valueChanges.subscribe(value => {
            this.timesPasswordTouched++;
            // The first time this gets touched is when initially setting receiverType.
            // this checks if it has been touched in addition to that on existing receivers.
            // If so, we reset the password and set it to a text field to unmask it.
            if (!this.passwordTouched && this.timesPasswordTouched > 1 && this.receiver.id) {
                this.passwordTouched = true;
                this.form.get("passwd").setValue(null);
            }
        });
    }

    shouldShowField(field) {
        return field.showField && !this.isRapidResopnseAndUsernamePasswordField(field);
    }

    isRapidResopnseAndUsernamePasswordField(field) {
        const receiverType = this.form.controls.receiverType.value;
        return (
            (receiverType === ReceiverTypes.rapidRspWest || receiverType === ReceiverTypes.rapidRspEast) &&
            (field.controlName === "username" || field.controlName === "passwd")
        );
    }

    private getDNISFromReceiver(receiver: AlulaReceiver): string {
        const recvNum = this.fillUpValue(receiver?.recvNum || "", "00");
        const lineNum = this.fillUpValue(receiver?.lineNum || "", "000");
        return recvNum + lineNum;
    }

    get receiverFields() {
        return this.form?.controls;
    }

    get checkFormValid() {
        return this.form.valid;
    }

    filterNumbers5Digits(fieldName: string) {
        const pattern = /[^0-9]/g;
        const control = this.receiverFields[fieldName];
        control.setValue(control.value.replace(pattern, "").substring(0, 5));
    }

    blurHandler(field: string) {
        const theField = this.receiverFields?.[field];
        const { value } = theField;
        const valueStr = "" + value;
        if (valueStr?.length < 5) {
            // fill up the dnis to match 5 digits
            const newVal = this.fillUpValue(valueStr, "00000");
            theField.setValue(newVal, { emitEvent: false });
        }
    }

    getFieldType(field: ReceiverField) {
        if (field.type === "password" && (!this.receiver.id || this.passwordTouched)) {
            return "text";
        }
        return field.type;
    }

    fillUpValue(val = "", template = "") {
        const stringVal = val + "";
        if (stringVal.length < template.length) {
            return template.substring(stringVal.length) + stringVal;
        }
        return stringVal + "";
    }

    saveReceiver() {
        const { dnis, ...receiver } = this.form.getRawValue();
        if (this.form.valid) {
            const receiverType = this.receiverFields.receiverType.value;
            if (receiverType === ReceiverTypes.sgtcp) {
                const nums = this.splitReceiverAndLineNumber(dnis);
                receiver.lineNum = nums.lineNum + "";
                receiver.recvNum = nums.recvNum + "";
            }
            receiver.dealerId = this.dealerId;
            this.emitter.emit({
                receiver: {
                    ...receiver,
                    receiverType:
                        receiverType === ReceiverTypes.rapidRspWest || receiverType === ReceiverTypes.rapidRspEast
                            ? ReceiverTypes.stages
                            : receiverType,
                },
            });
        }
    }
    removeIpReceiver() {
        if (this.form.valid) {
            this.emitter.emit({
                receiver: this.receiver,
                removeReceiver: true,
            });
        }
    }

    private splitReceiverAndLineNumber(dnis: string): { lineNum: string; recvNum: string } {
        if (dnis) {
            return { recvNum: dnis.substring(0, 2), lineNum: dnis.substring(2, 5) };
        }
        return null;
    }

    private updateFormFieldForRapidResponse() {
        this.form.controls["passwd"]?.disable();
        this.form.controls["username"]?.disable();
        this.form.controls["path"].disable();
        this.passwordTouched = true;
    }

    cancel() {
        this.emitter.emit(null);
    }
}
